import type { LobbyGame } from '../api/getLobby';
import { hasEntryClosed, isDynamicFillEligibleStatus } from './lobbyPlayableStatus';

export type UpcomingWindow = '7d' | '14d' | '30d';

export function upcomingWindowDays(window: string): number | null {
  if (window === '7d') return 7;
  if (window === '14d') return 14;
  if (window === '30d') return 30;
  return null;
}

export function parseContestStartUtc(startUtc: string | null | undefined): Date | null {
  if (!startUtc?.trim()) return null;
  const d = new Date(startUtc);
  return Number.isNaN(d.getTime()) ? null : d;
}

function startOfUtcDay(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}

function endOfUpcomingWindow(now: Date, days: number): Date {
  const end = startOfUtcDay(now);
  end.setUTCDate(end.getUTCDate() + days);
  end.setUTCHours(23, 59, 59, 999);
  return end;
}

export function hasContestStartPassed(
  startUtc: string | null | undefined,
  now = new Date()
): boolean {
  const start = parseContestStartUtc(startUtc);
  if (!start) return false;
  return start <= now;
}

function isDailyRevealLobbyGameType(gameType: string | null | undefined): boolean {
  const gt = (gameType ?? '').trim().toLowerCase().replace(/\s+/g, '_').replace(/-+/g, '_');
  return gt === 'daily_reveal' || gt === 'daily_draw';
}

export function isUpcomingWithinWindow(
  game: Pick<LobbyGame, 'start_utc' | 'game_type'>,
  window: UpcomingWindow,
  now = new Date()
): boolean {
  const start = parseContestStartUtc(game.start_utc);
  if (!start) return false;
  const days = upcomingWindowDays(window);
  if (!days) return false;
  const windowEnd = endOfUpcomingWindow(now, days);

  if (isDailyRevealLobbyGameType(game.game_type)) {
    if (start > now) return start <= windowEnd;
    const windowStart = startOfUtcDay(now);
    windowStart.setUTCDate(windowStart.getUTCDate() - (days - 1));
    return start >= windowStart && start <= windowEnd;
  }

  if (start <= now) return false;
  return start <= windowEnd;
}

export function sortGamesByUpcomingStart<T extends Pick<LobbyGame, 'start_utc' | 'display_order'>>(
  games: T[]
): T[] {
  return [...games].sort((a, b) => {
    const aStart = parseContestStartUtc(a.start_utc)?.getTime() ?? Number.POSITIVE_INFINITY;
    const bStart = parseContestStartUtc(b.start_utc)?.getTime() ?? Number.POSITIVE_INFINITY;
    if (aStart !== bStart) return aStart - bStart;
    return a.display_order - b.display_order;
  });
}

export function filterUpcomingByDateGames<
  T extends Pick<
    LobbyGame,
    'start_utc' | 'external_url' | 'display_order' | 'status' | 'game_type' | 'entry_close_utc'
  >,
>(games: T[], _window?: UpcomingWindow): T[] {
  // Custom games carry no lifecycle status, so the status-only check would drop every one
  // of them from date-ordered rows — their Start date & time decides instead. An
  // already-started custom game cannot be entered, so it is filtered out here too.
  //
  // The status checks alone would still let a contest whose door has shut be filled into a
  // row — a survivor past week 1's lock stays ACTIVE for the rest of the season — so the
  // entry deadline is tested here as well, for every game type.
  return sortGamesByUpcomingStart(
    games.filter(game => {
      if (hasEntryClosed(game)) return false;
      return game.external_url?.trim()
        ? !hasContestStartPassed(game.start_utc)
        : isDynamicFillEligibleStatus(game.status);
    })
  );
}
